import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PlayCircle, Loader2, Headset, LifeBuoy } from 'lucide-react';
import { Link } from 'react-router-dom';
import { api } from '@/api/client';

interface Chapter { t: number; title: string }
interface Video { lang: 'hi' | 'en'; title: string; durationSec: number; chapters: Chapter[]; available: boolean; url: string | null }

const LANG_LABEL: Record<string, string> = { hi: 'हिंदी', en: 'English' };
const mmss = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;

/**
 * The partner-portal tutorial, played inside the portal.
 *
 * Hindi and English versions of the same walkthrough, with subtitles burned in. The video itself is in
 * Cloudflare R2; `GET /help/videos` hands back a fresh signed link each time the page loads. Chapters
 * jump straight to a module, so a merchant who only wants "how do I raise a ticket" does not sit
 * through eight minutes to find it.
 */
export function TutorialVideo() {
  const { data, isLoading } = useQuery<{ videos: Video[] }>({
    queryKey: ['help-videos'],
    queryFn: async () => (await api.get('/help/videos')).data,
    staleTime: 60 * 60_000,
    retry: false,
  });
  const videos = (data?.videos ?? []).filter((v) => v.available && v.url);
  const [lang, setLang] = useState<'hi' | 'en'>(() => {
    try { return (localStorage.getItem('tutorial-lang') as 'hi' | 'en') || 'hi'; } catch { return 'hi'; }
  });
  const video = videos.find((v) => v.lang === lang) ?? videos[0];
  const ref = useRef<HTMLVideoElement>(null);
  const [now, setNow] = useState(0);
  useEffect(() => { try { localStorage.setItem('tutorial-lang', lang); } catch { /* private mode */ } }, [lang]);

  const jump = (t: number) => {
    const el = ref.current;
    if (!el) return;
    el.currentTime = t;
    el.play().catch(() => { /* autoplay blocked — the frame still moves */ });
  };
  const activeIdx = video ? video.chapters.reduce((acc, c, i) => (now >= c.t - 0.2 ? i : acc), 0) : 0;

  if (isLoading) {
    return (
      <div className="mb-6 rounded-xl border border-slate-200 bg-white p-8 text-center text-slate-400">
        <Loader2 size={20} className="mx-auto animate-spin" />
      </div>
    );
  }
  if (!video) return null; // not uploaded yet — the written help below still works

  return (
    <div className="mb-6 rounded-xl border border-slate-200 bg-white overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-slate-100">
        <div className="flex items-center gap-2">
          <PlayCircle size={20} className="text-primary" />
          <div>
            <div className="font-semibold text-slate-900">Video tutorial — how to use this portal</div>
            <div className="text-xs text-slate-500">{mmss(video.durationSec)} · module by module · subtitles included</div>
          </div>
        </div>
        {videos.length > 1 && (
          <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden">
            {videos.map((v) => (
              <button key={v.lang} onClick={() => setLang(v.lang)}
                className={`px-3 py-1.5 text-sm ${v.lang === video.lang ? 'bg-primary text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}>
                {LANG_LABEL[v.lang] ?? v.lang}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="grid lg:grid-cols-[1fr_260px]">
        <video key={video.url!} ref={ref} src={video.url!} controls playsInline preload="metadata"
          onTimeUpdate={(e) => setNow((e.target as HTMLVideoElement).currentTime)}
          className="w-full aspect-video bg-black" />
        {video.chapters.length > 0 && (
          <div className="border-t lg:border-t-0 lg:border-l border-slate-100 max-h-[420px] overflow-y-auto">
            <div className="px-3 pt-3 pb-1 text-[11px] font-semibold uppercase text-slate-400">Jump to</div>
            {video.chapters.map((c, i) => (
              <button key={i} onClick={() => jump(c.t)}
                className={`w-full flex items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-slate-50 ${i === activeIdx && now > 0 ? 'text-primary font-medium bg-primary-50' : 'text-slate-700'}`}>
                <span className="text-[11px] text-slate-400 w-10 shrink-0 tabular-nums">{mmss(c.t)}</span>
                <span className="truncate">{c.title}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="px-4 py-2.5 border-t border-slate-100 bg-slate-50 text-xs text-slate-600 flex flex-wrap items-center gap-x-4 gap-y-1">
        <span>Still stuck?</span>
        <Link to="/tickets" className="inline-flex items-center gap-1 text-primary hover:underline"><LifeBuoy size={13} /> Raise a support ticket</Link>
        <span className="inline-flex items-center gap-1"><Headset size={13} /> or call your support manager — name and number are at the top of every page</span>
      </div>
    </div>
  );
}
